import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { Room, ROOM_GUESTS_MAX } from '@shared/base-search-bar/entities/room.entities';
import { FLIGHT_PASSENGER_MAX, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { validChildrenAges } from '@ux/mappers/guest.mapper';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { ButtonLinkType } from '@design-system/components/molecules/button-link/entities/button-link.types';

@Component({
  selector: 'app-search-bar-room',
  templateUrl: './search-bar-room.component.html',
  styleUrls: ['./search-bar-room.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchBarRoomComponent implements OnChanges, OnDestroy {

  @ViewChild('searchRoomContainer') public searchRoomContainer: ElementRef;

  @Input() id: string = '';
  @Input() label: string;
  @Input() icon: Icon;
  @Input() placeholder: string;
  @Input() control: UntypedFormControl;
  @Input() intialRooms: Room[];
  @Input() isMobile: boolean;
  @Input() eventPath: EventTarget[];
  @Input() isHome: boolean;
  @Input() searchType: SearchType;
  @Input() packageFlightClass: FlightClass;
  @Input() packageStep: PackageStep;
  @Input() showClassSelector: boolean;
  @Input() searchBy: string = SearchBy.HOTELS;
  @Input() isHotelDetail: boolean = false;

  @Output() emitRoomsSelection: EventEmitter<Room[]> = new EventEmitter<Room[]>();
  @Output() setGuestsPerRoom: EventEmitter<Room> = new EventEmitter<Room>();
  @Output() fetchMessageAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() addNewRoom: EventEmitter<Room> = new EventEmitter<Room>();
  @Output() removeRoom: EventEmitter<number> = new EventEmitter<number>();
  @Output() isShowed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitPackageFlightClassSelection: EventEmitter<FlightClass> = new EventEmitter<FlightClass>();

  public show: boolean = false;
  public adults: string = TravelerTypes.ADULTS;
  public children: string = TravelerTypes.CHILDREN;
  public hotelRooms: Room[] = [];
  public maxHeight: number = 80;
  public componentIdentifier: string = 'search-room-item-';
  public componentIdentifierChildAges: string = '-search-room-item-';

  public packagesFlightClassTypes: FlightClass[] = [
    FlightClass.CHEAPER, FlightClass.PREMIUM, FlightClass.EXECUTIVE, FlightClass.FIRST_CLASS];
  public maxGuestPerRoom: number = ROOM_GUESTS_MAX;

  public readonly addCircleOutline = Icon.AddCircleOutline;
  public readonly minusCircleOutline = Icon.MinusCircleOutline;

  public readonly addOrRemoveRoomLabelSize = TypographyFontSize.XXS;

  public readonly buttonLinkType = ButtonLinkType.Secondary;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  constructor(
    protected cdr: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.intialRooms) {
      const rooms = [];
      this.intialRooms.forEach(data => rooms.push(
        {
          [TravelerTypes.ADULTS]: data[TravelerTypes.ADULTS],
          [TravelerTypes.CHILDREN]: data[TravelerTypes.CHILDREN],
          childrenAges: [].concat(data.childrenAges),
          id: data.id
        }));
      this.hotelRooms = rooms;
    }
    if (changes.eventPath) {
      const inputButton = changes.eventPath.currentValue.find(paths =>
        !!paths['classList'] && paths['classList'].length > 0
        && paths['classList'].contains('searchBarRoom'));
      if (!inputButton && this.show) {
        this.toggle();
      }
    }
  }

  ngOnDestroy(): void {
    this.cdr.detach();
  }

  get activateClassSelector(): boolean {
    return !this.isMobile && !!this.showClassSelector;
  }

  get isPackagesHome(): boolean {
    return this.activateClassSelector && this.searchType === SearchType.PACKAGES_HOME;
  }

  get isNotHotelPackageView(): boolean {
    return !this.isMobile && this.searchType === SearchType.PACKAGES_RESULT &&
      this.packageStep === PackageStep.FLIGHT_RESULTS;
  }

  get isHotelPackageView(): boolean {
    return !this.isMobile && this.searchType === SearchType.PACKAGES_RESULT &&
      this.packageStep === PackageStep.HOTEL_RESULTS;
  }

  get passengerType(): typeof PassengerType {
    return PassengerType;
  }

  get swipeScrollStyles(): { [key: string]: string } {
    return this.isMobile ? { 'max-height': `${this.maxHeight}vh` } : {};
  }

  get maxRoomsAllowed(): number {
    return this.searchBy === SearchBy.HOTELS ? 10 : 5
  }

  public showAddRoom(): boolean {
    const canAdd = this.searchBy === SearchBy.PACKAGES ? this.totalPassengers() < FLIGHT_PASSENGER_MAX : true;
    return this.hotelRooms.length < this.maxRoomsAllowed && canAdd;
  }

  public toggle(state: boolean = !this.show): void {
    if ((!state && this.validateChildrenAges()) || state) {
      this.show = state;
      if (this.show) {
        this.isShowed.emit();
      }
    }
  }

  public externalOpenRooms(): void {
    this.show = true;
    this.cdr.detectChanges(); this.cdr.markForCheck();
  }

  public close(): void {
    this.toggle(false);
  }

  public setRoomQuantity(quantity: number, type: PassengerType, index: number): void {
    if (type === this.passengerType.ADULTS) {
      this.hotelRooms[index][TravelerTypes.ADULTS] = quantity;
    } else {
      this.hotelRooms[index][TravelerTypes.CHILDREN] = quantity;
      this.hotelRooms[index].childrenAges.splice(quantity, 1);
    }
    this.setGuestsPerRoom.emit(this.hotelRooms[index]);
  }

  public roomTotalPassengers(room: Room): number {
    return !!room && room[TravelerTypes.ADULTS] + room[TravelerTypes.CHILDREN];
  }

  public totalPassengers(): number {
    const adults = this.hotelRooms.reduce((a, b) => a + b[TravelerTypes.ADULTS], 0);
    const children = this.hotelRooms.reduce((a, b) => a + b[TravelerTypes.CHILDREN], 0);
    return adults + children;
  }

  public applyRooms(): void {
    if (this.validateChildrenAges()) {
      this.emitRoomsSelection.emit(this.hotelRooms);
      this.close();
    } else this.fetchMessageAction.emit();
  }

  public setChildAges(selection: OptionSelected, index: number): void {
    this.hotelRooms[index].childrenAges[selection.index] = parseInt(selection.value.toString());
    this.setGuestsPerRoom.emit(this.hotelRooms[index]);
  }

  public validateChildrenAges(): boolean {
    return validChildrenAges(this.hotelRooms);
  }

  public addRoom(): void {
    if (this.showAddRoom()) {
      const newRoom = {
        id: this.hotelRooms.length,
        [TravelerTypes.ADULTS]: this.totalPassengers() + 2 > FLIGHT_PASSENGER_MAX ? 1 : 2,
        [TravelerTypes.CHILDREN]: 0,
        childrenAges: []
      };
      this.hotelRooms.push(newRoom);
      this.addNewRoom.emit(newRoom);
      this.searchRoomContainer.nativeElement.scrollTop = this.searchRoomContainer.nativeElement.scrollHeight;
    }
  }

  public roomNumber(index: number): number {
    return index + 1;
  }

  public canRemove(index: number): boolean {
    return index > 0;
  }

  public isActiveClass(classType: FlightClass): boolean {
    return classType === this.packageFlightClass;
  }

  public selectPackageFlightClass(flightClass: FlightClass): void {
    this.emitPackageFlightClassSelection.emit(flightClass);
  }

}
